import React, { useCallback, useRef, useState } from 'react';
import { 
  Paper,
  Typography,
  Box,
  styled,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import AddModuleModal from './AddModuleModal';
import ModuleSekmesi from './ModuleSekmesi';
import SortableModuleTabs from './SortableModuleTabs';
import { useTheme } from '@mui/material/styles';
import {
  getProductModules,
  saveProductModule,
  deleteProductModule,
} from '../../../services/ecommerceApi';
import type { CustomModule, ModuleSettings } from '../ECommercePage';
import { applyHomepageModuleTemplate, DEFAULT_BRAND_FLOW_SETTINGS, type HomepageModuleTemplateId } from './homepageModuleTemplates';
import HomePageSectionsPanel from './homepageSections/HomePageSectionsPanel';
import { useTranslation } from 'react-i18next';

// ECommercePage'den kopyalandı
const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(1),
    }
}));

// Tab paneli için yardımcı bileşen
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`homepage-tabpanel-${index}`}
      aria-labelledby={`homepage-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface HomePageSettingsProps {
  companyId: string;
  configId: string; // EcommerceConfig.Id (SQL)
  config?: any; // ECommerce config
  onMenuSettingChange?: (setting: 'enabled' | 'menuType' | 'menuPosition' | 'verticalPadding' | 'backgroundColor' | 'textColor' | 'textTransform' | 'showAllCategories' | 'showHomePage' | 'menuStyle' | 'moduleType', value: any) => void; // Menu ayarları değişiklik fonksiyonu
}

const defaultModuleSettings: ModuleSettings = {
  enabled: true,
  displayStyle: { web: 'carousel', mobile: 'carousel' },
  titleStyles: {
    fontSize: 24,
    color: '#111827',
    bold: true,
    italic: false,
    fontFamily: 'Inter',
    textAlign: 'center',
  },
  htmlContent: '',
  selectedProductIds: [],
  selectedCategoryIds: [],
  productSource: 'manual',
  moduleTemplate: 'default',
  showViewAllButton: false,
  viewAllButtonText: 'Tümü',
  categoryProductLimit: 3,
  maxProducts: 12,
  maxCategories: 12,
  itemsPerRow: 4,
  horizontalPosition: 50,
  topSpacing: 0,
  moduleImage: {
    imageUrl: '',
    width: 600,
    height: 400,
    alignment: 'center',
    moduleDisplayMode: 'grid',
    moduleSliderAutoplay: false,
    moduleSliderInterval: 4000,
    moduleSliderShowArrows: true,
    moduleSliderLoop: true,
    moduleSliderPauseOnHover: true,
    moduleSliderVisibleCount: 4,
    pageSidePadding: 44,
    productLayout: {
      rows: 2,
      columns: 2
    },
    productCardSize: {
      width: 200,
      height: 250
    },
    productCardStyle: {
      cardEnabled: true,
      showProductName: true,
      showPrice: true,
      showOldPrice: true,
      brandTabsEnabled: false,
      categoryTabsEnabled: false,
      productGrid: {
        alignment: 'center',
        horizontalGap: 12,
        verticalGap: 12
      },
      displayMode: 'grid',
      sliderAutoplay: false,
      sliderInterval: 4000,
      sliderShowArrows: true,
      sliderLoop: true,
      sliderPauseOnHover: true,
      sliderVisibleCount: 6,
      productNameColor: '#ffffff',
      productNameFontSize: 14,
      productNameBold: false,
      productNameItalic: false,
      priceColor: '#fbbf24',
      priceFontSize: 14,
      priceBold: true,
      priceItalic: false
    }
  },
  productCardStyle: {
    size: { width: 280, height: 384 },
    imageHeight: 192,
    cardSettings: {
      showProductName: true,
      showSku: true,
      showPrice: true,
      showStockStatus: true,
      showAddToCartButton: true,
      lowStockThreshold: 5,
      lowStockWarning: false,
    },
    elementStyles: {
      productName: { fontSize: '0.875rem', fontWeight: 600, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
      sku: { fontSize: '0.75rem', fontWeight: 400, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
      price: { fontSize: '1.125rem', fontWeight: 'bold', color: '#1976d2', fontStyle: 'normal', fontFamily: 'Arial' },
      stockStatus: { fontSize: '0.75rem', fontWeight: 400, color: 'inherit', fontStyle: 'normal', fontFamily: 'Arial' },
    },
    imageNavigationStyle: 'numbered',
  },
};

const MODULE_TAB_OFFSET = 2;

const HomePageSettingsComponent: React.FC<HomePageSettingsProps> = ({ companyId, configId, config, onMenuSettingChange }) => {
  const { t } = useTranslation('salesSystem');
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<CustomModule | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);
  const [deleteModuleName, setDeleteModuleName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customModules, setCustomModules] = useState<CustomModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  const theme = useTheme();

  const buildModulePayload = useCallback((module: CustomModule) => ({
    id: module.id,
    configId,
    moduleType: 'custom',
    tabName: module.tabName,
    sectionTitle: module.sectionTitle,
    displayOrder: module.order,
    isActive: module.settings?.enabled !== false,
    settingsJson: JSON.stringify(module.settings),
    selectedProductIds: JSON.stringify(module.settings.selectedProductIds || []),
    selectedCategoryIds: JSON.stringify(module.settings.selectedCategoryIds || []),
    showViewAllButton: module.settings.showViewAllButton,
    viewAllButtonText: module.settings.viewAllButtonText || 'Tümü',
    maxProducts: module.settings.maxProducts,
    maxCategories: module.settings.maxCategories || 12,
    horizontalPosition: module.settings.horizontalPosition || 50,
    itemsPerRow: module.settings.itemsPerRow || 4,
  }), [configId]);

  // Modülleri yükle (SQL)
  React.useEffect(() => {
    const loadModules = async () => {
      if (!companyId) return;

      try {
        setLoading(true);
        console.log('🔄 [MODULES] Modüller SQL den yükleniyor...', { companyId });
        
        const response = await getProductModules(companyId, 'custom');
        console.log('📥 [MODULES] SQL den gelen modüller:', response);
        
        if (response && response.data) {
          // SQL'den gelen modülleri CustomModule formatına çevir
          const modules: CustomModule[] = response.data.map((module: any) => {
            const parsedSettings = module.settingsJson ? JSON.parse(module.settingsJson) : {};
            let columnCategoryIds: string[] = [];
            try {
              columnCategoryIds = module.selectedCategoryIds ? JSON.parse(module.selectedCategoryIds) : [];
            } catch {
              columnCategoryIds = [];
            }
            return {
              id: module.id,
              tabName: module.tabName || 'Yeni Modül',
              sectionTitle: module.sectionTitle ?? '',
              order: module.displayOrder || 0,
              settings: {
                ...defaultModuleSettings,
                ...parsedSettings,
                selectedCategoryIds: parsedSettings.selectedCategoryIds?.length
                  ? parsedSettings.selectedCategoryIds
                  : columnCategoryIds,
              },
            };
          });
          
          setCustomModules(modules.sort((a, b) => a.order - b.order));
          console.log('✅ [MODULES] Modüller başarıyla yüklendi:', modules.length, 'modül');
        }
      } catch (error) {
        console.error('❌ [MODULES] Modüller yüklenirken hata:', error);
        setError('Modüller yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, [companyId]);

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const syncActiveTabAfterReorder = (normalizedModules: CustomModule[]) => {
    if (activeTab < MODULE_TAB_OFFSET) return;

    const activeModuleId = customModules[activeTab - MODULE_TAB_OFFSET]?.id;
    if (!activeModuleId) return;

    const nextIndex = normalizedModules.findIndex((module) => module.id === activeModuleId);
    if (nextIndex >= 0) {
      setActiveTab(nextIndex + MODULE_TAB_OFFSET);
    }
  };

  const handleModulesReorder = async (reorderedModules: CustomModule[]) => {
    const normalizedModules = reorderedModules.map((module, index) => ({
      ...module,
      order: index,
    }));
    const previousModules = customModules;

    setCustomModules(normalizedModules);
    syncActiveTabAfterReorder(normalizedModules);

    try {
      setReordering(true);
      setError(null);
      await Promise.all(
        normalizedModules.map((module) => saveProductModule(buildModulePayload(module)))
      );
    } catch (reorderError) {
      console.error('❌ [REORDER MODULES] Modül sırası kaydedilirken hata:', reorderError);
      setCustomModules(previousModules);
      setError(t('ecommerceHomeSettings.reorderError'));
    } finally {
      setReordering(false);
    }
  };
  
  const handleAddModule = async (tabName: string, sectionTitle: string, template: HomepageModuleTemplateId = 'default') => {
    try {
      console.log('➕ [ADD MODULE] Yeni modül ekleniyor...', { tabName, sectionTitle, configId, template });
      const moduleSettings = applyHomepageModuleTemplate(defaultModuleSettings, template);
      
      const moduleData = {
        configId: configId, // EcommerceConfig.Id
        moduleType: 'custom',
        tabName: tabName,
        sectionTitle: sectionTitle,
        displayOrder: customModules.length,
        isActive: true,
        settingsJson: JSON.stringify(moduleSettings),
        selectedProductIds: JSON.stringify(moduleSettings.selectedProductIds || []),
        selectedCategoryIds: JSON.stringify(moduleSettings.selectedCategoryIds || []),
        maxCategories: 12,
        horizontalPosition: 50,
        itemsPerRow: template === 'category-row' ? 3 : 4,
        showViewAllButton: moduleSettings.showViewAllButton,
        viewAllButtonText: moduleSettings.viewAllButtonText,
        maxProducts: moduleSettings.maxProducts,
      };
      
      const response = await saveProductModule(moduleData);
      console.log('✅ [ADD MODULE] Modül başarıyla eklendi:', response);
      
      if (response && response.data) {
        const newModule: CustomModule = {
          id: response.data.id,
          tabName,
          sectionTitle,
          settings: moduleSettings,
          order: customModules.length,
        };
        
        const updatedModules = [...customModules, newModule];
        setCustomModules(updatedModules);
        
        setActiveTab(updatedModules.length - 1 + MODULE_TAB_OFFSET);
      }
    } catch (error) {
      console.error('❌ [ADD MODULE] Modül eklenirken hata:', error);
      setError('Modül eklenirken bir hata oluştu.');
    }
  };

  const moduleSaveGen = useRef(0);

  const handleModuleUpdate = async (updatedModule: CustomModule) => {
    // Yazarken eski kaydın state'i ezmesini önle: önce local, sonra SQL.
    setCustomModules((prev) =>
      prev.map((module) => (module.id === updatedModule.id ? updatedModule : module))
    );
    const gen = ++moduleSaveGen.current;
    try {
      await saveProductModule(buildModulePayload(updatedModule));
    } catch (error) {
      console.error('❌ [UPDATE MODULE] Modül güncellenirken hata:', error);
      if (gen === moduleSaveGen.current) {
        setError('Modül güncellenirken bir hata oluştu.');
      }
    }
  };

  const handleDeleteModule = (moduleId: string, moduleName: string) => {
    setModuleToDelete(moduleId);
    setDeleteModuleName(moduleName);
    setDeleteModalOpen(true);
  };

  const confirmDeleteModule = async () => {
    if (!moduleToDelete || !companyId) return;

    setDeleting(true);
    setError(null);

    try {
      console.log('🗑️ [DELETE MODULE] Modül siliniyor...', { id: moduleToDelete });
      
      // SQL'den sil
      const response = await deleteProductModule({
        id: moduleToDelete
      });
      
      console.log('✅ [DELETE MODULE] Modül başarıyla silindi:', response);
      
      // Local state'i güncelle
      const updatedModules = customModules.filter(
        (module) => module.id !== moduleToDelete
      );
      setCustomModules(updatedModules);
      
      // Güvenli bir sekmeye geçiş yap
      setActiveTab(0);
      
      // Modal'ı kapat
      setDeleteModalOpen(false);
      setModuleToDelete(null);
      setDeleteModuleName('');
      
    } catch (error) {
      console.error('❌ [DELETE MODULE] Modül silinirken hata:', error);
      setError('Modül silinirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditModule = (module: CustomModule) => {
    setEditingModule(module);
    setEditModalOpen(true);
  };

  const handleUpdateModuleName = async (tabName: string, sectionTitle: string) => {
    if (!editingModule) return;
    
    try {
      console.log('✏️ [EDIT MODULE] Modül adı güncelleniyor...', { id: editingModule.id, tabName, sectionTitle });
      
      const updatedModule = {
        ...editingModule,
        tabName: tabName,
        sectionTitle: sectionTitle
      };
      
      await handleModuleUpdate(updatedModule);
      
      setEditModalOpen(false);
      setEditingModule(null);
      
      console.log('✅ [EDIT MODULE] Modül adı başarıyla güncellendi');
    } catch (error) {
      console.error('❌ [EDIT MODULE] Modül adı güncellenirken hata:', error);
      setError('Modül adı güncellenirken bir hata oluştu.');
    }
  };

  const cancelDeleteModule = () => {
    setDeleteModalOpen(false);
    setModuleToDelete(null);
    setDeleteModuleName('');
    setError(null);
  };

  const processedModules = customModules.map(module => {
    const newSettings = {
        ...defaultModuleSettings,
        ...(module.settings || {}),
        displayStyle: {
            ...defaultModuleSettings.displayStyle,
            ...(module.settings?.displayStyle || {}),
        },
        titleStyles: {
            ...defaultModuleSettings.titleStyles,
            ...(module.settings?.titleStyles || {}),
        },
        ...(module.settings?.moduleTemplate === 'brand-flow'
          ? {
              brandFlow: {
                ...DEFAULT_BRAND_FLOW_SETTINGS,
                ...(module.settings?.brandFlow || {}),
                subtitleStyles: {
                  ...DEFAULT_BRAND_FLOW_SETTINGS.subtitleStyles,
                  ...(module.settings?.brandFlow?.subtitleStyles || {}),
                },
                footerStyles: {
                  ...DEFAULT_BRAND_FLOW_SETTINGS.footerStyles,
                  ...(module.settings?.brandFlow?.footerStyles || {}),
                },
              },
            }
          : {}),
    };

    return {
        ...module,
        settings: newSettings
    };
  });

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <SectionTitle variant="h6">
          <HomeIcon /> Ana Sayfa Ayarları
        </SectionTitle>
        <Typography>{t('ecommerceHomeSettings.loadingModules')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      <SectionTitle variant="h6" sx={{ p: 3, pb: 1 }}>
        <HomeIcon /> {t('ecommerceHomeSettings.title')}
      </SectionTitle>
      
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 2 }}>
        {t('ecommerceHomeSettings.subtitle')}
      </Typography>

      {error && !deleteModalOpen && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200],
      }}>
          <SortableModuleTabs
            generalLabel={t('ecommerceHomeSettings.tabs.general')}
            fixedTabs={[{ label: 'Sayfada Gösterilecek Kısımlar' }]}
            activeTab={activeTab}
            modules={processedModules}
            reordering={reordering}
            dragHandleTitle={t('ecommerceHomeSettings.dragToReorder')}
            onSelectTab={handleTabChange}
            onReorder={handleModulesReorder}
          />
          {reordering && (
            <CircularProgress size={18} sx={{ ml: 1, flexShrink: 0 }} />
          )}
          <Tooltip title={t('ecommerceHomeSettings.actions.addNewModule')}>
              <IconButton
                size="small"
                sx={{ ml: 1, flexShrink: 0 }}
                onClick={() => setIsModalOpen(true)}
                disabled={reordering}
              >
                  <AddIcon />
              </IconButton>
          </Tooltip>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Typography variant="h6" gutterBottom>
          {t('ecommerceHomeSettings.general.title')}
        </Typography>
        
        {/* Ana Sayfa Modül Stili */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('ecommerceHomeSettings.general.moduleStyleTitle')}
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.8rem', mb: 1, fontWeight: 'normal', color: 'text.secondary' }}>
                {t('ecommerceHomeSettings.general.moduleStyleHelp')}
              </FormLabel>
              <RadioGroup
                row
                name="moduleType"
                value={config?.menuSettings?.moduleType || 'module1'}
                onChange={(e) => onMenuSettingChange?.('moduleType', e.target.value)}
              >
                <FormControlLabel value="module1" control={<Radio />} label={t('ecommerceHomeSettings.general.module1')} />
                <FormControlLabel value="module2" control={<Radio />} label={t('ecommerceHomeSettings.general.module2')} />
              </RadioGroup>
            </FormControl>
            <Divider sx={{ my: 2 }} />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('ecommerceHomeSettings.trustStrip.movedToSliderHint')}
        </Alert>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('ecommerceHomeSettings.general.description')}
        </Typography>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('ecommerceHomeSettings.general.info')}
        </Alert>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <HomePageSectionsPanel
          companyId={companyId}
          configId={configId}
          homepageModules={processedModules}
        />
      </TabPanel>

      {processedModules.map((module, index) => (
        <TabPanel key={module.id} value={activeTab} index={index + MODULE_TAB_OFFSET}>
          <ModuleSekmesi 
            module={module}
            onDelete={() => handleDeleteModule(module.id, module.tabName)}
            onModuleUpdate={handleModuleUpdate}
            onEditModule={() => handleEditModule(module)}
            companyId={companyId}
          />
        </TabPanel>
      ))}

      <AddModuleModal 
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddModule}
      />

      {/* Modül Düzenleme Modal'ı */}
      <AddModuleModal 
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingModule(null);
        }}
        onAdd={handleAddModule}
        editMode={true}
        initialTabName={editingModule?.tabName || ''}
        initialSectionTitle={editingModule?.sectionTitle || ''}
        onUpdate={handleUpdateModuleName}
      />

      {/* Modül Silme Onay Modal'ı */}
      <Dialog
        open={deleteModalOpen}
        onClose={!deleting ? cancelDeleteModule : undefined}
        aria-labelledby="delete-module-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-module-dialog-title">
          {t('ecommerceHomeSettings.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ecommerceHomeSettings.deleteDialog.question', { name: deleteModuleName })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main', fontWeight: 'medium' }}>
            {t('ecommerceHomeSettings.deleteDialog.warning')}
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDeleteModule} 
            disabled={deleting}
            color="primary"
          >
            {t('ecommerceHomeSettings.actions.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteModule} 
            disabled={deleting}
            color="error"
            variant="contained"
            autoFocus
          >
            {deleting ? t('ecommerceHomeSettings.actions.deleting') : t('ecommerceHomeSettings.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HomePageSettingsComponent;
